import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type {
  HTMLAttributes,
  KeyboardEvent as ReactKeyboardEvent,
  ReactNode,
  RefObject,
} from "react";
import "./Sheet.css";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

function readDurationToken(token: string, fallback: number): number {
  if (typeof window === "undefined") return fallback;
  const raw = window
    .getComputedStyle(document.documentElement)
    .getPropertyValue(token)
    .trim();
  const value = Number.parseFloat(raw);
  if (!Number.isFinite(value)) return fallback;
  return raw.endsWith("ms") ? value : value * 1000;
}

/** Same body dataset counter as Dialog, so a sheet over a dialog cooperates. */
function lockScroll() {
  const next = Number(document.body.dataset["fcScrollLocks"] ?? "0") + 1;
  document.body.dataset["fcScrollLocks"] = String(next);
  if (next === 1) {
    document.body.dataset["fcScrollPrev"] = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }
}

function unlockScroll() {
  const next = Math.max(
    0,
    Number(document.body.dataset["fcScrollLocks"] ?? "1") - 1,
  );
  if (next === 0) {
    document.body.style.overflow = document.body.dataset["fcScrollPrev"] ?? "";
    delete document.body.dataset["fcScrollPrev"];
    delete document.body.dataset["fcScrollLocks"];
    return;
  }
  document.body.dataset["fcScrollLocks"] = String(next);
}

export interface SheetProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "title" | "children"
> {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  footer?: ReactNode;
  children?: ReactNode;
  /** Height cap as a share of the viewport. `full` is the 92% snap point. */
  size?: "auto" | "half" | "full";
  initialFocus?: RefObject<HTMLElement | null>;
  closeOnBackdropClick?: boolean;
  /** The grab handle is decorative but always visible; this hides it. */
  showHandle?: boolean;
}

/**
 * The mobile counterpart of `Dialog`: same API, slides up from the bottom
 * edge. Drag-to-dismiss is not implemented — the handle is the affordance and
 * Escape, the backdrop and any footer action all close it.
 */
export function Sheet({
  open,
  onClose,
  title,
  description,
  footer,
  children,
  size = "auto",
  initialFocus,
  closeOnBackdropClick = true,
  showHandle = true,
  className,
  ...rest
}: SheetProps) {
  const uid = useId();
  const titleId = `${uid}title`;
  const descriptionId = `${uid}desc`;

  const panelRef = useRef<HTMLDivElement | null>(null);
  const restoreRef = useRef<HTMLElement | null>(null);

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const [mounted, setMounted] = useState(open);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      let inner = 0;
      const outer = window.requestAnimationFrame(() => {
        inner = window.requestAnimationFrame(() => setVisible(true));
      });
      return () => {
        window.cancelAnimationFrame(outer);
        window.cancelAnimationFrame(inner);
      };
    }

    setVisible(false);
    const timer = window.setTimeout(
      () => setMounted(false),
      readDurationToken("--fc-duration-quick", 140),
    );
    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!mounted) return undefined;

    restoreRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    lockScroll();

    const panel = panelRef.current;
    const target =
      initialFocus?.current ??
      panel?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR) ??
      panel;
    target?.focus();

    const onDocumentKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onCloseRef.current();
      }
    };
    document.addEventListener("keydown", onDocumentKeyDown);

    return () => {
      document.removeEventListener("keydown", onDocumentKeyDown);
      unlockScroll();
      restoreRef.current?.focus();
    };
  }, [mounted, initialFocus]);

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Tab") return;
    const panel = panelRef.current;
    if (panel === null) return;

    const nodes = Array.from(
      panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
    );
    const first = nodes[0];
    const last = nodes[nodes.length - 1];

    if (first === undefined || last === undefined) {
      event.preventDefault();
      panel.focus();
      return;
    }

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  if (!mounted || typeof document === "undefined") return null;

  const panelClasses = [
    "fc-sheet__panel",
    `fc-sheet__panel--${size}`,
    className,
  ]
    .filter((value): value is string => Boolean(value))
    .join(" ");

  return createPortal(
    <div
      className={visible ? "fc-sheet is-open" : "fc-sheet is-closing"}
      onKeyDown={handleKeyDown}
    >
      <div
        className="fc-sheet__backdrop"
        aria-hidden="true"
        onClick={closeOnBackdropClick ? onClose : undefined}
      />
      <div
        {...rest}
        ref={panelRef}
        className={panelClasses}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description !== undefined ? descriptionId : undefined}
        tabIndex={-1}
      >
        {showHandle ? (
          <span className="fc-sheet__handle" aria-hidden="true" />
        ) : null}

        <div className="fc-sheet__header">
          <h2 className="fc-sheet__title" id={titleId}>
            {title}
          </h2>
          {description !== undefined ? (
            <p className="fc-sheet__description" id={descriptionId}>
              {description}
            </p>
          ) : null}
        </div>

        {children !== undefined && children !== null ? (
          <div className="fc-sheet__body">{children}</div>
        ) : null}

        {footer !== undefined && footer !== null ? (
          <div className="fc-sheet__footer">{footer}</div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
